/**
  This function applies the accordion to a given node element using provided options.
  @param {HTMLElement} node - the node element to apply the accordion to.
  @param {Object} options - an object containing options for the initial state of the accordion, the duration of the animation, the opacity, and the easing function.
  @param {boolean} options.isOpen - the initial state of the accordion.
  @param {number} options.duration - the duration of the animation in milliseconds.
  @param {number} [options.opacity] - the opacity level during the accordion transition.
  @param {string | function} [options.easing] - the easing function or name used for the animation.
  @returns {Object} - an object with an update method to animate the height of the accordion.

  INFO:
  It initializes the height of the accordion element based on the isOpen option.
  The update method updates the isAccordionOpen variable to keep track of the current state of the accordion.
  The function sets up a resize observer to monitor size changes in the node element.
  When the observer detects a change, the function updates the initial height of the element.
  The function uses the Web Animations API to animate the opening and closing of the accordion.
  If the accordion is closed, the function animates the accordion to collapse, updates the height and overflow properties, and cancels the animation at the end.
  If the accordion is open, the function animates the accordion to expand, updates the height and overflow properties, and cancels the animation at the end.
**/

export function accordion(
  node: HTMLElement,
  {
    isOpen,
    duration,
    opacity,
  }: {
    isOpen: boolean;
    duration: number;
    opacity?: number;
    easing?: string | ((t: number) => number);
  }
) {
  const DEFAULT_OPACITY = 0.85;
  const DEFAULT_EASING = "cubic-bezier(.67,.66,.55,.93)";

  let initialHeight = node.scrollHeight;
  let isAccordionOpen = isOpen;
  let hasMutated = false;

  // callback for the ResizeObserver to update initialHeight when content changes
  const callback = () => {
    if (!hasMutated) {
      hasMutated = true;
      initialHeight = node.scrollHeight; // update initialHeight after first mutation
    }
    setTimeout(
      () => {
        if (isAccordionOpen) {
          initialHeight = node.clientHeight; // adjust initialHeight when accordion is open and content changes
        }
      },
      duration + initialHeight / 8 // delay to ensure accurate height after animations
    );
  };

  const observer = new ResizeObserver(callback);
  observer.observe(node);

  // initialize styles based on accordion state
  node.style.height = isAccordionOpen ? "auto" : "0";
  node.style.overflow = isAccordionOpen ? "overlay" : "hidden";
  node.style.opacity = isAccordionOpen ? "1" : String(opacity);

  return {
    update(options: {
      isOpen: boolean;
      duration: number;
      opacity?: number;
      easing?: string | ((t: number) => number);
    }) {
      isAccordionOpen = options.isOpen;
      const opacity = options.opacity ?? DEFAULT_OPACITY;
      const easing = options.easing ?? DEFAULT_EASING;
      const easeFn = typeof easing === "function" ? easing : undefined;
      const easeString = typeof easing === "string" ? easing : undefined;

      const startHeight = node.clientHeight;
      const endHeight = isAccordionOpen ? initialHeight : 0;
      const heightDifference = endHeight - startHeight;

      const totalDuration = options.duration + initialHeight / 8;
      const startTime = performance.now();

      if (easeFn) {
        // custom animation using requestAnimationFrame and easing function
        const animate = (time: number) => {
          const elapsed = time - startTime;
          const progress = Math.min(elapsed / totalDuration, 1);
          const easedProgress = easeFn(progress); // apply easing function for smooth animation

          const currentHeight = startHeight + heightDifference * easedProgress;
          node.style.height = currentHeight + "px";

          // update opacity for a fade effect during the accordion transition
          const startOpacity = isAccordionOpen ? opacity : 1;
          const endOpacity = isAccordionOpen ? 1 : opacity;
          const opacityDifference = endOpacity - startOpacity;
          const currentOpacity = startOpacity + opacityDifference * easedProgress;
          node.style.opacity = String(currentOpacity);

          node.style.overflow = "hidden";

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // final adjustments after animation completes
            node.style.height = isAccordionOpen ? "auto" : "0";
            node.style.opacity = String(endOpacity);
            node.style.overflow = isAccordionOpen ? "overlay" : "hidden";
          }
        };
        requestAnimationFrame(animate);
      } else if (easeString) {
        // animation using the Web Animations API
        const animation: Animation = node.animate(
          [
            {
              height: startHeight + "px",
              opacity: isAccordionOpen ? opacity : 1,
              overflow: "hidden",
            },
            {
              height: endHeight + "px",
              opacity: isAccordionOpen ? 1 : opacity,
              overflow: "hidden",
            },
          ],
          {
            duration: totalDuration,
            fill: "both",
            easing: easeString,
          }
        );

        animation.onfinish = () => {
          // reset styles after animation ends
          node.style.height = isAccordionOpen ? "auto" : "0";
          node.style.opacity = isAccordionOpen ? "1" : String(opacity);
          node.style.overflow = isAccordionOpen ? "overlay" : "hidden";
          animation.cancel(); // cancel animation to free up resources
        };
      }
    },
    destroy() {
      observer.disconnect();
    },
  };
}
